'use client';
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { useSettings } from '@/hooks/useSettings';
import type { GameSettings } from '@/hooks/useSettings';

// ─── Sub-section components ───────────────────────────────────────────────────

function SectionHeader({ icon, title, subtitle }: { icon: React.ReactNode; title: string; subtitle: string }) {
  return (
    <div className="flex items-start gap-3 mb-5">
      <div className="w-9 h-9 rounded-xl bg-primary/15 border border-primary/30 flex items-center justify-center flex-shrink-0 mt-0.5">
        {icon}
      </div>
      <div>
        <h3 className="font-700 text-foreground text-base">{title}</h3>
        <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
      </div>
    </div>
  );
}

function ToggleSwitch({ enabled, onToggle }: { enabled: boolean; onToggle: () => void }) {
  return (
    <button
      onClick={onToggle}
      aria-checked={enabled}
      role="switch"
      className={`relative flex-shrink-0 w-12 h-6 rounded-full transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
        enabled ? 'bg-primary' : 'bg-muted'
      }`}
    >
      <span
        className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all duration-300 ${
          enabled ? 'left-6' : 'left-0.5'
        }`}
      />
    </button>
  );
}

// ─── Audio Section ────────────────────────────────────────────────────────────

function AudioSection({
  volume,
  onChange,
}: {
  volume: number;
  onChange: (v: number) => void;
}) {
  const isMuted = volume === 0;

  return (
    <div className="rounded-2xl border border-border bg-card p-6">
      <SectionHeader
        icon={
          <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M15.536 8.464a5 5 0 010 7.072M12 6v12m-3.536-9.536a5 5 0 000 7.072M9 12H3m18 0h-3" />
          </svg>
        }
        title="Audio Volume"
        subtitle="Controls dice rolls, piece placements, formations, and victory sounds."
      />

      <div className="flex items-center gap-4">
        {/* Mute icon */}
        <button
          onClick={() => onChange(isMuted ? 70 : 0)}
          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-secondary/60 transition-colors"
          aria-label={isMuted ? 'Unmute' : 'Mute'}
        >
          {isMuted ? (
            <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
            </svg>
          ) : (
            <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M15.536 8.464a5 5 0 010 7.072M12 6v12m-3.536-9.536a5 5 0 000 7.072" />
            </svg>
          )}
        </button>

        {/* Slider */}
        <div className="flex-1 relative">
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={volume}
            onChange={(e) => onChange(Number(e.target.value))}
            className="w-full h-2 rounded-full appearance-none cursor-pointer accent-primary bg-muted"
            style={{
              background: `linear-gradient(to right, var(--primary) 0%, var(--primary) ${volume}%, var(--muted) ${volume}%, var(--muted) 100%)`,
            }}
          />
        </div>

        {/* Value badge */}
        <span className="w-10 text-right font-700 text-sm text-foreground font-mono-game">
          {isMuted ? 'OFF' : `${volume}%`}
        </span>
      </div>

      {/* Volume level labels */}
      <div className="flex justify-between mt-2 px-12">
        {['Silent', 'Low', 'Mid', 'High', 'Max'].map((label) => (
          <span key={label} className="text-xs text-muted-foreground">{label}</span>
        ))}
      </div>
    </div>
  );
}

// ─── Animation Speed Section ──────────────────────────────────────────────────

const SPEED_OPTIONS: { value: GameSettings['animationSpeed']; label: string; desc: string }[] = [
  { value: 'slow', label: 'Slow', desc: '2× duration' },
  { value: 'normal', label: 'Normal', desc: 'Default' },
  { value: 'fast', label: 'Fast', desc: '0.5× duration' },
  { value: 'off', label: 'Off', desc: 'Instant' },
];

function AnimationSection({
  speed,
  onChange,
}: {
  speed: GameSettings['animationSpeed'];
  onChange: (v: GameSettings['animationSpeed']) => void;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-6">
      <SectionHeader
        icon={
          <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        }
        title="Animation Speed"
        subtitle="Affects dice rolls, piece drops, formation highlights, and transitions."
      />

      <div className="grid grid-cols-4 gap-2">
        {SPEED_OPTIONS.map((opt) => {
          const isActive = speed === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => onChange(opt.value)}
              className={`py-3 px-2 rounded-xl border text-center transition-all duration-150 active:scale-95 ${
                isActive
                  ? 'border-primary/60 bg-primary/10 text-primary' :'border-border bg-secondary/30 text-muted-foreground hover:border-primary/30 hover:text-foreground'
              }`}
            >
              <div className="font-700 text-sm">{opt.label}</div>
              <div className="text-xs mt-0.5 opacity-70">{opt.desc}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Visual Theme Section ─────────────────────────────────────────────────────

function ThemeSection({
  theme,
  onChange,
}: {
  theme: GameSettings['visualTheme'];
  onChange: (v: GameSettings['visualTheme']) => void;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-6">
      <SectionHeader
        icon={
          <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M17.657 17.657l-.707-.707M6.343 6.343l-.707-.707M12 8a4 4 0 100 8 4 4 0 000-8z" />
          </svg>
        }
        title="Visual Theme"
        subtitle="Switch between dark and light board aesthetics."
      />

      <div className="grid grid-cols-2 gap-3">
        {/* Dark theme card */}
        <button
          onClick={() => onChange('dark')}
          className={`rounded-xl border p-4 text-left transition-all duration-150 active:scale-95 ${
            theme === 'dark' ?'border-primary/60 bg-primary/10' :'border-border bg-secondary/30 hover:border-primary/30'
          }`}
        >
          {/* Preview swatch */}
          <div className="w-full h-16 rounded-lg mb-3 overflow-hidden relative"
            style={{ background: '#0F0F1A' }}>
            <div className="absolute inset-0 flex items-center justify-center gap-2">
              <div className="w-6 h-6 rounded border border-white/20" style={{ background: '#161525' }} />
              <div className="w-6 h-6 rounded border border-purple-500/60" style={{ background: '#7C3AED33' }} />
              <div className="w-6 h-6 rounded border border-amber-500/60" style={{ background: '#F59E0B33' }} />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <div className="font-700 text-sm text-foreground">Dark</div>
              <div className="text-xs text-muted-foreground mt-0.5">Deep space aesthetic</div>
            </div>
            {theme === 'dark' && (
              <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
            )}
          </div>
        </button>

        {/* Light theme card */}
        <button
          onClick={() => onChange('light')}
          className={`rounded-xl border p-4 text-left transition-all duration-150 active:scale-95 ${
            theme === 'light' ?'border-primary/60 bg-primary/10' :'border-border bg-secondary/30 hover:border-primary/30'
          }`}
        >
          {/* Preview swatch */}
          <div className="w-full h-16 rounded-lg mb-3 overflow-hidden relative"
            style={{ background: '#F8F7FF' }}>
            <div className="absolute inset-0 flex items-center justify-center gap-2">
              <div className="w-6 h-6 rounded border border-gray-300" style={{ background: '#FFFFFF' }} />
              <div className="w-6 h-6 rounded border border-purple-400" style={{ background: '#7C3AED22' }} />
              <div className="w-6 h-6 rounded border border-amber-400" style={{ background: '#F59E0B22' }} />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <div className="font-700 text-sm text-foreground">Light</div>
              <div className="text-xs text-muted-foreground mt-0.5">Clean board surface</div>
            </div>
            {theme === 'light' && (
              <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
            )}
          </div>
        </button>
      </div>

      {theme === 'light' && (
        <p className="mt-3 text-xs text-amber-400/80 flex items-center gap-1.5">
          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          Light theme applies to the board canvas. Full app theming coming soon.
        </p>
      )}
    </div>
  );
}

// ─── Rule Presets Section ─────────────────────────────────────────────────────

const RULE_CONFIGS = [
  {
    key: 'balancingPhase' as const,
    name: 'Balancing Phase',
    description: 'After all pieces are placed, players protect and remove pieces. Recalculates formations.',
    badge: 'Recommended',
    badgeColor: 'bg-accent/20 text-accent border-accent/30',
  },
  {
    key: 'texasTieBreak' as const,
    name: 'Texas Tie Break',
    description: 'Tied scores resolved by total formation count. Prevents draws in competitive play.',
    badge: 'Recommended',
    badgeColor: 'bg-accent/20 text-accent border-accent/30',
  },
  {
    key: 'sweetwaterRule' as const,
    name: 'Sweetwater Rule',
    description: "Restricts access to Spot 9 unless it\'s a critical strategic move.",
    badge: 'Official',
    badgeColor: 'bg-primary/20 text-secondary-foreground border-primary/30',
  },
  {
    key: 'chunnelRule' as const,
    name: 'Chunnel Rule',
    description: 'Enables diagonal connections through Spot 9, creating extra formation paths.',
    badge: 'Official',
    badgeColor: 'bg-primary/20 text-secondary-foreground border-primary/30',
  },
];

type RulesState = GameSettings['rules'];

function RulesSection({
  rules,
  onChange,
}: {
  rules: RulesState;
  onChange: (r: RulesState) => void;
}) {
  const applyPreset = (preset: 'standard' | 'competitive' | 'casual') => {
    if (preset === 'standard') {
      onChange({ balancingPhase: true, texasTieBreak: true, sweetwaterRule: false, chunnelRule: false });
    } else if (preset === 'competitive') {
      onChange({ balancingPhase: true, texasTieBreak: true, sweetwaterRule: true, chunnelRule: false });
    } else {
      onChange({ balancingPhase: false, texasTieBreak: false, sweetwaterRule: false, chunnelRule: false });
    }
  };

  const toggle = (key: keyof RulesState) => {
    onChange({ ...rules, [key]: !rules[key] });
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-6">
      <SectionHeader
        icon={
          <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        }
        title="Rule Presets"
        subtitle="These defaults apply when starting a new game from the home screen."
      />

      {/* Preset buttons */}
      <div className="flex gap-2 mb-5">
        {[
          { id: 'standard' as const, label: 'Standard', desc: 'Balancing + Tie Break' },
          { id: 'competitive' as const, label: 'Competitive', desc: '+ Sweetwater' },
          { id: 'casual' as const, label: 'Casual', desc: 'No special rules' },
        ].map((preset) => (
          <button
            key={preset.id}
            onClick={() => applyPreset(preset.id)}
            className="flex-1 py-2.5 px-2 rounded-xl border border-border bg-secondary/40 hover:border-primary/40 hover:bg-secondary/70 active:scale-95 transition-all duration-150 text-center"
          >
            <div className="font-600 text-xs text-foreground">{preset.label}</div>
            <div className="text-xs text-muted-foreground mt-0.5 hidden sm:block">{preset.desc}</div>
          </button>
        ))}
      </div>

      {/* Individual toggles */}
      <div className="flex flex-col gap-3">
        {RULE_CONFIGS.map((rule) => {
          const isEnabled = rules[rule.key];
          return (
            <div
              key={rule.key}
              className={`rounded-xl border p-4 transition-all duration-200 ${
                isEnabled ? 'border-primary/40 bg-primary/5' : 'border-border bg-secondary/20'
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-700 text-sm text-foreground">{rule.name}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-600 border ${rule.badgeColor}`}>
                      {rule.badge}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{rule.description}</p>
                </div>
                <ToggleSwitch enabled={isEnabled} onToggle={() => toggle(rule.key)} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Active summary */}
      <div className="mt-4 p-3 rounded-xl bg-secondary/50 border border-border">
        <div className="text-xs font-600 text-muted-foreground uppercase tracking-wider mb-2">Active Defaults</div>
        <div className="flex flex-wrap gap-1.5">
          {RULE_CONFIGS.filter((r) => rules[r.key]).map((r) => (
            <span key={r.key} className="px-2.5 py-1 rounded-lg text-xs font-600 bg-primary/20 text-secondary-foreground">
              {r.name}
            </span>
          ))}
          {RULE_CONFIGS.every((r) => !rules[r.key]) && (
            <span className="text-xs text-muted-foreground">No special rules — standard play</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Settings Client ─────────────────────────────────────────────────────

export default function SettingsClient() {
  const router = useRouter();
  const { settings, loaded, updateSettings, resetSettings } = useSettings();
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  if (!loaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Nav */}
      <nav className="flex items-center justify-between px-6 py-5 lg:px-12 border-b border-border bg-card/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="flex items-center gap-4">
          <button
            onClick={() => router.back()}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span className="text-sm font-500 hidden sm:block">Back</span>
          </button>
          <div className="w-px h-5 bg-border" />
          <a href="/" className="flex items-center gap-2.5">
            <AppLogo size={28} />
            <span className="font-700 text-foreground text-base">ViNCi</span>
          </a>
        </div>
        <h1 className="font-700 text-foreground text-lg">Settings</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { resetSettings(); setSaved(true); setTimeout(() => setSaved(false), 2000); }}
            className="px-3 py-1.5 rounded-xl border border-border text-xs font-600 text-muted-foreground hover:text-foreground hover:border-primary/40 transition-all duration-150"
          >
            Reset
          </button>
          <button
            onClick={handleSave}
            className={`px-4 py-1.5 rounded-xl text-xs font-700 transition-all duration-200 ${
              saved
                ? 'bg-green-500/20 text-green-400 border border-green-500/40' :'bg-primary text-primary-foreground hover:bg-primary/90'
            }`}
          >
            {saved ? '✓ Saved' : 'Save'}
          </button>
        </div>
      </nav>

      {/* Content */}
      <div className="flex-1 px-6 py-8 lg:px-12">
        <div className="max-w-2xl mx-auto space-y-5">

          {/* Page header */}
          <div className="mb-8">
            <p className="text-muted-foreground text-sm leading-relaxed">
              Customise your ViNCi experience. Settings are saved automatically and apply to new games.
              Rule presets can still be overridden during game setup.
            </p>
          </div>

          {/* Audio */}
          <AudioSection
            volume={settings.audioVolume}
            onChange={(v) => updateSettings({ audioVolume: v })}
          />

          {/* Animation */}
          <AnimationSection
            speed={settings.animationSpeed}
            onChange={(v) => updateSettings({ animationSpeed: v })}
          />

          {/* Theme */}
          <ThemeSection
            theme={settings.visualTheme}
            onChange={(v) => updateSettings({ visualTheme: v })}
          />

          {/* Rules */}
          <RulesSection
            rules={settings.rules}
            onChange={(r) => updateSettings({ rules: r })}
          />

          {/* Bottom CTA */}
          <div className="pt-4 flex flex-col sm:flex-row gap-3">
            <a
              href="/game-setup"
              className="flex-1 py-3.5 rounded-2xl bg-primary text-primary-foreground font-700 text-center hover:bg-primary/90 active:scale-95 transition-all duration-150 glow-primary"
            >
              Start New Game →
            </a>
            <a
              href="/game-board"
              className="flex-1 py-3.5 rounded-2xl border border-border text-foreground font-600 text-center hover:bg-secondary/50 active:scale-95 transition-all duration-150"
            >
              Resume Game
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
